/**
 * PatrolDetailModal
 *
 * Modal dialog showing detailed patrol information including:
 * - Patrol header (site, guard, status, time)
 * - Progress bar with checkpoint count
 * - Collapsible checklists with checkpoint details
 * - Complete patrol action (for in-progress patrols)
 *
 * Visual Design:
 * ┌─────────────────────────────────────────┐
 * │ [X]  Patrol Details                     │
 * ├─────────────────────────────────────────┤
 * │ ┌─────────────────────────────────────┐ │
 * │ │ 📍 Site Name                        │ │
 * │ │ Guard: John Smith                   │ │
 * │ │ Status: In Progress ●               │ │
 * │ │ Started: 2:30 PM                    │ │
 * │ └─────────────────────────────────────┘ │
 * │ Progress ████████░░░░░░░░░ 5/12        │
 * │ ▼ Checklist: Exterior Patrol           │
 * │   ✓ Front entrance - Clear             │
 * │   ⚠ Back door - Warning: "Door ajar"   │
 * ├─────────────────────────────────────────┤
 * │              [Complete Patrol]          │
 * └─────────────────────────────────────────┘
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Tooltip,
  Collapse,
  Divider,
  Modal,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CommentIcon from '@mui/icons-material/Comment';
import EventIcon from '@mui/icons-material/Event';
import ListIcon from '@mui/icons-material/List';
import classNames from 'classnames';
import styles from './PatrolDetailModal.module.css';
import {
  usePatrolDetail,
  TransformedChecklist,
  TransformedCheckpoint,
  CheckpointDisplayStatus,
} from '../../hooks/dispatch/usePatrolDetail';
import { formatRelativeTime } from './ActivityFeed';
import { parseApiDateTime, formatApiTime, formatApiDateTime } from 'utils/dateUtils';
import CompletePatrolConfirmDialog from './CompletePatrolConfirmDialog';
import CheckpointEditModal from './CheckpointEditModal';

// ============================================
// Types
// ============================================

export type PatrolStatus = 'pending' | 'in_progress' | 'completed';

export interface PatrolDetailModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Schedule assignment ID for fetching details */
  scheduleAssignmentId: number | null;
  /** Patrol ID for complete action */
  patrolId: number | null;
  /** Guard name to display */
  guardName: string;
  /** Site name to display */
  siteName: string;
  /** Current patrol status */
  patrolStatus: PatrolStatus;
  /** Patrol start time */
  startedAt?: string | null;
  /** Callback to complete the patrol */
  onCompletePatrol?: (patrolId: number, note?: string) => Promise<void>;
  /** Whether complete mutation is in progress */
  isCompleting?: boolean;
  /** Enable checkpoint editing (for dispatchers) */
  allowEdit?: boolean;
}

// ============================================
// Constants
// ============================================

/** Status chip colors */
const STATUS_COLORS: Record<PatrolStatus, 'default' | 'primary' | 'success'> = {
  pending: 'default',
  in_progress: 'primary',
  completed: 'success',
};

/** Status display labels */
const STATUS_LABELS: Record<PatrolStatus, string> = {
  pending: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
};

/** Checkpoint status icons */
const STATUS_ICONS: Record<CheckpointDisplayStatus, React.ElementType> = {
  clear: CheckCircleIcon,
  warning: WarningIcon,
  emergency: ErrorIcon,
  info: InfoIcon,
  incomplete: RadioButtonUncheckedIcon,
};

// ============================================
// Sub-components
// ============================================

/** Response type labels for display */
const RESPONSE_TYPE_LABELS: Record<string, string> = {
  Comment: 'Comment',
  Status: 'Status',
  Time: 'Time',
  DateTime: 'Date & Time',
  Dropdown: 'Selection',
  DropdownMultiSelect: 'Multiple Selection',
  Attachment: 'Attachment',
  Signature: 'Signature',
};

export interface CheckpointItemProps {
  checkpoint: TransformedCheckpoint;
  /** Controlled expanded state */
  expanded: boolean;
  /** Callback when expand state changes */
  onExpandChange: () => void;
  /** Whether editing is allowed */
  allowEdit?: boolean;
  /** Callback when edit button is clicked */
  onEditClick?: () => void;
}

// Type for attachment viewing modal
interface ViewingAttachment {
  AttachmentID: number;
  Name: string;
  previewUrl: string;
  isImage: boolean;
  isVideo: boolean;
}

export const CheckpointItem: React.FC<CheckpointItemProps> = ({
  checkpoint,
  expanded,
  onExpandChange,
  allowEdit,
  onEditClick,
}) => {
  const [viewingAttachment, setViewingAttachment] = useState<ViewingAttachment | null>(null);
  const [attachmentLoading, setAttachmentLoading] = useState(true);
  const [attachmentError, setAttachmentError] = useState(false);
  const Icon = STATUS_ICONS[checkpoint.displayStatus];
  const hasComment = checkpoint.comment && checkpoint.comment.trim().length > 0;
  const hasAttachments = Array.isArray(checkpoint.attachments) && checkpoint.attachments.length > 0;
  const hasDropdownSelections = Array.isArray(checkpoint.selectedDropdownIds) && checkpoint.selectedDropdownIds.length > 0;

  // Format time display (using UTC-aware parsing for API datetimes)
  const timeDisplay = checkpoint.dateTime
    ? formatApiTime(checkpoint.dateTime)
    : checkpoint.time || null;

  // Format full datetime display (using UTC-aware parsing for API datetimes)
  const dateTimeDisplay = checkpoint.dateTime
    ? formatApiDateTime(checkpoint.dateTime, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  // Get selected dropdown names
  const selectedDropdownNames = useMemo(() => {
    if (!hasDropdownSelections || !checkpoint.dropdowns || !Array.isArray(checkpoint.dropdowns)) return [];
    if (!checkpoint.selectedDropdownIds || !Array.isArray(checkpoint.selectedDropdownIds)) return [];
    return checkpoint.selectedDropdownIds
      .map(id => checkpoint.dropdowns.find(d => d.DropdownID === id)?.Name)
      .filter(Boolean);
  }, [checkpoint.dropdowns, checkpoint.selectedDropdownIds, hasDropdownSelections]);

  // Determine if there are details to show
  const hasDetails = hasComment || hasAttachments || hasDropdownSelections || dateTimeDisplay || checkpoint.description;

  return (
    <Box
      className={styles.checkpointItem}
      sx={{
        // Visual feedback when expanded
        backgroundColor: expanded ? (theme) => `${theme.palette.primary.main}0A` : 'transparent',
        borderRadius: 1,
        transition: 'background-color 0.2s ease',
      }}
    >
      {/* Clickable Header Row */}
      <Box
        className={styles.checkpointClickable}
        onClick={onExpandChange}
        sx={{ cursor: 'pointer' }}
      >
        <Box className={classNames(styles.checkpointIcon, styles[checkpoint.displayStatus])}>
          <Icon />
        </Box>
        <Box className={styles.checkpointContent}>
          <Box className={styles.checkpointHeader}>
            <Typography className={styles.checkpointName}>
              {checkpoint.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {timeDisplay && (
                <Typography className={styles.checkpointTime}>
                  {timeDisplay}
                </Typography>
              )}
              {hasDetails && (
                <IconButton size="small" sx={{ padding: '2px' }}>
                  {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              )}
            </Box>
          </Box>
          {checkpoint.selectedStatus && (
            <Typography className={styles.checkpointResponse}>
              {checkpoint.selectedStatus.Name}
            </Typography>
          )}
          {/* Preview of comment in collapsed state */}
          {!expanded && hasComment && (
            <Typography
              className={styles.checkpointCommentPreview}
              sx={{
                fontSize: '0.8125rem',
                color: 'text.secondary',
                fontStyle: 'italic',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              "{checkpoint.comment.length > 50 ? checkpoint.comment.substring(0, 50) + '...' : checkpoint.comment}"
            </Typography>
          )}
        </Box>
      </Box>

      {/* Expanded Details */}
      <Collapse in={expanded}>
        <Box className={styles.checkpointDetails}>
          {/* Description */}
          {checkpoint.description && (
            <Box className={styles.detailSection}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                Description
              </Typography>
              <Typography variant="body2">{checkpoint.description}</Typography>
            </Box>
          )}

          {/* Response Type */}
          <Box className={styles.detailSection}>
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
              Response Type
            </Typography>
            <Chip
              label={RESPONSE_TYPE_LABELS[checkpoint.responseTypeName] || checkpoint.responseTypeName}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Box>

          {/* Full DateTime */}
          {dateTimeDisplay && (
            <Box className={styles.detailSection}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EventIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                  Date & Time
                </Typography>
              </Box>
              <Typography variant="body2">{dateTimeDisplay}</Typography>
            </Box>
          )}

          {/* Time only (if no datetime) */}
          {!dateTimeDisplay && checkpoint.time && (
            <Box className={styles.detailSection}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                  Time
                </Typography>
              </Box>
              <Typography variant="body2">{checkpoint.time}</Typography>
            </Box>
          )}

          {/* Dropdown Selections */}
          {hasDropdownSelections && selectedDropdownNames.length > 0 && (
            <Box className={styles.detailSection}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ListIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                  Selected Options
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {selectedDropdownNames.map((name, idx) => (
                  <Chip key={idx} label={name} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          {/* Full Comment */}
          {hasComment && (
            <Box className={styles.detailSection}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CommentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                  Comment
                </Typography>
              </Box>
              <Box
                className={classNames(styles.checkpointComment, {
                  [styles.warning]: checkpoint.displayStatus === 'warning',
                  [styles.emergency]: checkpoint.displayStatus === 'emergency',
                })}
              >
                <Typography className={styles.commentText}>
                  "{checkpoint.comment}"
                </Typography>
              </Box>
            </Box>
          )}

          {/* Attachments */}
          {hasAttachments && (
            <Box className={styles.detailSection}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                  Attachments ({checkpoint.attachments?.length || 0})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {(checkpoint.attachments || []).map((attachment) => {
                  const isImage = attachment.ContentType?.includes('image');
                  const isVideo = attachment.ContentType?.includes('video');
                  const previewUrl = `/api/fileuploadservice/Preview?FileId=${attachment.AttachmentID}`;

                  return (
                    <Box
                      key={attachment.AttachmentID}
                      onClick={() => {
                        if (isImage || isVideo) {
                          setViewingAttachment({ ...attachment, previewUrl, isImage, isVideo });
                        }
                      }}
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: (isImage || isVideo) ? 'pointer' : 'default',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'grey.100',
                        position: 'relative',
                        '&:hover': (isImage || isVideo) ? {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        } : {},
                      }}
                    >
                      {isImage ? (
                        <img
                          src={previewUrl}
                          alt={attachment.Name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            // On error, show placeholder icon
                            (e.target as HTMLImageElement).style.display = 'none';
                            (e.target as HTMLImageElement).nextElementSibling?.removeAttribute('style');
                          }}
                        />
                      ) : isVideo ? (
                        <VideocamIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                      ) : (
                        <InsertDriveFileIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                      )}
                      {isImage && (
                        <ImageIcon
                          sx={{ fontSize: 32, color: 'text.secondary', display: 'none' }}
                        />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          backgroundColor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          padding: '2px 4px',
                          fontSize: '0.65rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {attachment.Name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Edit Button with Warning */}
          {allowEdit && onEditClick && (
            <Box className={styles.editSection}>
              <Divider sx={{ my: 1.5 }} />
              <Alert
                severity="warning"
                sx={{
                  py: 0.5,
                  '& .MuiAlert-message': { width: '100%' },
                }}
                action={
                  <Button
                    color="warning"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditClick();
                    }}
                  >
                    Edit
                  </Button>
                }
              >
                <Typography variant="body2">
                  Editing will modify the guard's original response
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Attachment Viewer Modal */}
      <Modal
        open={viewingAttachment !== null}
        onClose={() => {
          setViewingAttachment(null);
          setAttachmentLoading(true);
          setAttachmentError(false);
        }}
        aria-labelledby="attachment-viewer-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {viewingAttachment && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                <Typography variant="subtitle2" id="attachment-viewer-title">
                  {viewingAttachment.Name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setViewingAttachment(null);
                    setAttachmentLoading(true);
                    setAttachmentError(false);
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              {attachmentLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {attachmentError && (
                <Typography color="error" sx={{ p: 2 }}>
                  Failed to load {viewingAttachment.isImage ? 'image' : 'video'}.
                </Typography>
              )}
              {viewingAttachment.isImage && (
                <img
                  src={viewingAttachment.previewUrl}
                  alt={viewingAttachment.Name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: 'calc(90vh - 80px)',
                    objectFit: 'contain',
                    display: attachmentLoading ? 'none' : 'block',
                  }}
                  onLoad={() => setAttachmentLoading(false)}
                  onError={() => {
                    setAttachmentLoading(false);
                    setAttachmentError(true);
                  }}
                />
              )}
              {viewingAttachment.isVideo && (
                <video
                  src={viewingAttachment.previewUrl}
                  controls
                  style={{
                    maxWidth: '100%',
                    maxHeight: 'calc(90vh - 80px)',
                    display: attachmentLoading ? 'none' : 'block',
                  }}
                  onLoadedData={() => setAttachmentLoading(false)}
                  onError={() => {
                    setAttachmentLoading(false);
                    setAttachmentError(true);
                  }}
                />
              )}
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export interface ChecklistAccordionProps {
  checklist: TransformedChecklist;
  /** Controlled expanded state */
  expanded: boolean;
  /** Callback when accordion expand state changes */
  onExpandChange: (expanded: boolean) => void;
  /** Whether editing is allowed */
  allowEdit?: boolean;
  /** Callback when a checkpoint's edit button is clicked */
  onEditCheckpoint?: (checkpoint: TransformedCheckpoint) => void;
}

export const ChecklistAccordion: React.FC<ChecklistAccordionProps> = ({
  checklist,
  expanded,
  onExpandChange,
  allowEdit = false,
  onEditCheckpoint,
}) => {
  // Track which checkpoint is expanded (only one at a time)
  const [expandedCheckpointId, setExpandedCheckpointId] = useState<number | null>(null);

  // Handle checkpoint expand - collapse others
  const handleCheckpointExpand = (checkpointId: number) => {
    setExpandedCheckpointId((prev) => (prev === checkpointId ? null : checkpointId));
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => onExpandChange(isExpanded)}
      className={styles.checklistAccordion}
      disableGutters
      sx={{
        // Visual hierarchy: subtle background when expanded
        backgroundColor: expanded ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
        transition: 'background-color 0.2s ease',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={styles.checklistSummary}
      >
        <Box className={styles.checklistHeader}>
          <ChecklistIcon className={styles.checklistIcon} />
          <Typography className={styles.checklistName}>
            {checklist.name}
          </Typography>
          <Typography
            className={classNames(styles.checklistProgress, {
              [styles.complete]: checklist.isComplete,
            })}
          >
            {checklist.completedCount}/{checklist.totalCount}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails className={styles.checklistDetails}>
        {/* Visual hierarchy: left border for checkpoint container */}
        <Box
          sx={{
            borderLeft: '3px solid',
            borderColor: 'primary.main',
            ml: 1,
            pl: 1.5,
          }}
        >
          {(checklist.checkpoints || []).map((checkpoint) => (
            <CheckpointItem
              key={checkpoint.checkpointId}
              checkpoint={checkpoint}
              expanded={expandedCheckpointId === checkpoint.checkpointId}
              onExpandChange={() => handleCheckpointExpand(checkpoint.checkpointId)}
              allowEdit={allowEdit}
              onEditClick={onEditCheckpoint ? () => onEditCheckpoint(checkpoint) : undefined}
            />
          ))}
        </Box>
        {(!checklist.checkpoints || checklist.checkpoints.length === 0) && (
          <Box className={styles.emptyState}>
            <Typography variant="body2" color="textSecondary">
              No checkpoints in this checklist
            </Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

// ============================================
// Main Component
// ============================================

const PatrolDetailModal: React.FC<PatrolDetailModalProps> = ({
  open,
  onClose,
  scheduleAssignmentId,
  patrolId,
  guardName,
  siteName,
  patrolStatus,
  startedAt,
  onCompletePatrol,
  isCompleting = false,
  allowEdit = false,
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [editingCheckpoint, setEditingCheckpoint] = useState<TransformedCheckpoint | null>(null);
  // Track which checklist is expanded (only one at a time, collapse siblings)
  const [expandedChecklistId, setExpandedChecklistId] = useState<number | null>(null);

  // Fetch patrol details
  const {
    checklists,
    progress,
    generalComment,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = usePatrolDetail({
    scheduleAssignmentId,
    enabled: open && scheduleAssignmentId !== null,
  });

  // Expand first checklist by default when checklists first load
  // Only runs once per modal open (when checklists change from empty to populated)
  const hasInitializedRef = React.useRef(false);

  React.useEffect(() => {
    if (checklists.length > 0 && !hasInitializedRef.current) {
      setExpandedChecklistId(checklists[0].checklistId);
      hasInitializedRef.current = true;
    }
  }, [checklists]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setExpandedChecklistId(null);
      hasInitializedRef.current = false;
    }
  }, [open]);

  // Handle checklist expand - collapse others
  const handleChecklistExpand = useCallback((checklistId: number, isExpanded: boolean) => {
    setExpandedChecklistId(isExpanded ? checklistId : null);
  }, []);

  // Determine if patrol can be completed
  const canComplete = patrolStatus === 'in_progress' && patrolId !== null && onCompletePatrol;
  const hasIncompleteCheckpoints = progress.completedCheckpoints < progress.totalCheckpoints;

  // Handle complete button click
  const handleCompleteClick = useCallback(() => {
    if (hasIncompleteCheckpoints) {
      // Show confirmation dialog for incomplete patrols
      setShowConfirmDialog(true);
    } else {
      // Complete directly if all checkpoints done
      handleConfirmComplete();
    }
  }, [hasIncompleteCheckpoints]);

  // Handle confirmed completion
  const handleConfirmComplete = useCallback(async (note?: string) => {
    if (patrolId && onCompletePatrol) {
      try {
        await onCompletePatrol(patrolId, note);
        setShowConfirmDialog(false);
        onClose();
      } catch (err) {
        // Error handling is done in parent component
        console.error('[PatrolDetailModal/handleConfirmComplete] Failed to complete patrol:', err);
      }
    }
  }, [patrolId, onCompletePatrol, onClose]);

  // Close confirmation dialog
  const handleCloseConfirm = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  // Handle edit checkpoint click
  const handleEditCheckpoint = useCallback((checkpoint: TransformedCheckpoint) => {
    setEditingCheckpoint(checkpoint);
  }, []);

  // Handle edit modal close
  const handleCloseEditModal = useCallback(() => {
    setEditingCheckpoint(null);
  }, []);

  // Handle checkpoint saved - refetch data
  const handleCheckpointSaved = useCallback(() => {
    refetch();
  }, [refetch]);

  // Format started time (using UTC-aware parsing for API datetimes)
  const startedTimeDisplay = useMemo(() => {
    if (!startedAt) return null;
    return {
      time: formatApiTime(startedAt),
      relative: formatRelativeTime(startedAt),
    };
  }, [startedAt]);

  // Progress bar color
  const progressColor = progress.hasEmergencies
    ? 'error'
    : progress.hasWarnings
    ? 'warning'
    : 'primary';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="patrol-detail-title"
      >
        {/* Header Bar */}
        <div
          className={classNames(styles.headerBar, {
            [styles.inProgress]: patrolStatus === 'in_progress',
            [styles.completed]: patrolStatus === 'completed',
            [styles.pending]: patrolStatus === 'pending',
          })}
        />

        {/* Title */}
        <DialogTitle className={styles.dialogTitle} id="patrol-detail-title">
          <Typography className={styles.titleText}>Patrol Details</Typography>
          <IconButton
            onClick={onClose}
            className={styles.closeButton}
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          {/* Header Card */}
          <Box className={styles.headerCard}>
            <Typography className={styles.siteName}>
              <LocationOnIcon sx={{ fontSize: 20, verticalAlign: 'text-bottom', mr: 0.5 }} />
              {siteName}
            </Typography>

            <Box className={styles.detailRow}>
              <PersonIcon />
              <Typography className={styles.detailText}>{guardName}</Typography>
              <Chip
                label={STATUS_LABELS[patrolStatus]}
                color={STATUS_COLORS[patrolStatus]}
                size="small"
                className={styles.statusChip}
              />
            </Box>

            {startedTimeDisplay && (
              <Box className={styles.detailRow}>
                <AccessTimeIcon />
                <Tooltip title={startedTimeDisplay.relative}>
                  <Typography className={styles.detailText}>
                    Started: {startedTimeDisplay.time}
                  </Typography>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* General Comment */}
          {generalComment && generalComment.trim() && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'grey.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                Patrol Comment
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {generalComment}
              </Typography>
            </Box>
          )}

          {/* Loading State */}
          {isLoading && (
            <Box className={styles.loadingContainer}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="textSecondary">
                Loading patrol details...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {isError && (
            <Box className={styles.errorContainer}>
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => refetch()}>
                    Retry
                  </Button>
                }
              >
                {error?.message || 'Failed to load patrol details'}
              </Alert>
            </Box>
          )}

          {/* Content */}
          {!isLoading && !isError && (
            <>
              {/* Progress Section */}
              <Box className={styles.progressSection}>
                <Box className={styles.progressHeader}>
                  <Typography className={styles.progressLabel}>Progress</Typography>
                  <Typography className={styles.progressCount}>
                    {progress.completedCheckpoints} / {progress.totalCheckpoints} checkpoints
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress.percentComplete}
                  color={progressColor}
                  className={styles.progressBar}
                  classes={{ bar: styles.progressBarFill }}
                />
                {(progress.hasWarnings || progress.hasEmergencies) && (
                  <Box className={styles.alertBadges}>
                    {progress.hasWarnings && (
                      <Box className={classNames(styles.alertBadge, styles.warning)}>
                        <WarningIcon />
                        <span>Warning reported</span>
                      </Box>
                    )}
                    {progress.hasEmergencies && (
                      <Box className={classNames(styles.alertBadge, styles.emergency)}>
                        <ErrorIcon />
                        <span>Emergency reported</span>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              {/* Checklists */}
              <Box className={styles.checklistsSection}>
                {checklists.length === 0 ? (
                  <Box className={styles.emptyState}>
                    <AssignmentIcon />
                    <Typography variant="body2">
                      No checklists for this patrol
                    </Typography>
                  </Box>
                ) : (
                  checklists.map((checklist) => (
                    <ChecklistAccordion
                      key={checklist.checklistId}
                      checklist={checklist}
                      expanded={expandedChecklistId === checklist.checklistId}
                      onExpandChange={(isExpanded) => handleChecklistExpand(checklist.checklistId, isExpanded)}
                      allowEdit={allowEdit}
                      onEditCheckpoint={handleEditCheckpoint}
                    />
                  ))
                )}
              </Box>
            </>
          )}
        </DialogContent>

        {/* Actions */}
        {canComplete && (
          <DialogActions className={styles.dialogActions}>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCompleteClick}
              disabled={isCompleting || isFetching}
              startIcon={isCompleting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isCompleting ? 'Completing...' : 'Complete Patrol'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Confirmation Dialog */}
      <CompletePatrolConfirmDialog
        open={showConfirmDialog}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmComplete}
        isCompleting={isCompleting}
        incompleteCount={progress.totalCheckpoints - progress.completedCheckpoints}
        totalCount={progress.totalCheckpoints}
        guardName={guardName}
        siteName={siteName}
      />

      {/* Checkpoint Edit Modal */}
      {allowEdit && scheduleAssignmentId && (
        <CheckpointEditModal
          open={editingCheckpoint !== null}
          onClose={handleCloseEditModal}
          checkpoint={editingCheckpoint}
          scheduleAssignmentId={scheduleAssignmentId}
          onSaved={handleCheckpointSaved}
        />
      )}
    </>
  );
};

export default PatrolDetailModal;
